'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { direction, translate, type Language, type MessageKey } from '@/lib/i18n';

type Theme = 'light' | 'dark' | 'system';
interface Preferences { language: Language; setLanguage: (value: Language) => void; theme: Theme; setTheme: (value: Theme) => void; t: (key: MessageKey) => string; }
const Context = createContext<Preferences | null>(null);

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => (localStorage.getItem('futsaly-admin-language') as Language | null) ?? 'en');
  const [theme, setThemeState] = useState<Theme>(() => (localStorage.getItem('futsaly-admin-theme') as Theme | null) ?? 'system');
  useEffect(() => {
    const dark = theme === 'dark' || (theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    localStorage.setItem('futsaly-admin-theme', theme);
  }, [theme]);
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = direction(language);
    localStorage.setItem('futsaly-admin-language', language);
  }, [language]);
  const value = useMemo(() => ({ language, setLanguage: setLanguageState, theme, setTheme: setThemeState, t: (key: MessageKey) => translate(language, key) }), [language, theme]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function usePreferences() {
  const value = useContext(Context);
  if (!value) throw new Error('PreferencesProvider is missing');
  return value;
}
